import React from "react";
import * as attachmentService from "../../../../models/services/attachmentService";
import * as taskService from "../../../../models/services/taskService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TaskDynamicFormContainer } from "../../TaskDynamicFormContainer";
import { TaskDynamicForm } from "../../TaskDynamicForm/TaskDynamicForm";
import { ConfirmationDialogResult, TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "source/framework/appUtils";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { AttachmentList } from "source/components/attachments/AttachmentList/AttachmentList";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import downloadjs from "downloadjs";
import { SaveDataObj } from "source/framework/saveData";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { Alert, Box } from "@mui/material";

interface ITaskEditDialogFormViewProps {
    isNew: boolean;
    isLockedToOtherUser: boolean;
    isReadOnly: boolean;
    companyId: string;
    maxHeight?: string | number;
    taskId: string;
    taskType: ITaskEditTypeItem;
    taskDataRows: ITaskDataRow[];
    onChangeTaskDataRows: (taskDataRows: ITaskDataRow[]) => void;
}

export const TaskEditDialogFormView = (props: ITaskEditDialogFormViewProps) => {
    // #region Attachments
    const getAttachments = (): IFileAttachment[] => {
        return props.taskType.taskRepetitionType === TaskRepetitionType.NotRepeated && props.taskDataRows.length > 0 && props.taskDataRows[0].cols.length > 0 && props.taskDataRows[0].attachments.length > 0
            ? props.taskDataRows[0].attachments
            : [];
    };

    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = getAttachments().find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const getRemoveAttachmentsSaveData = (attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.taskId);
        result.append("companyId", props.companyId);
        result.append("ids", attachmentIds);
        return result;
    };

    const handleAttachmentRemove = async (id: string) => {
        if (!id) return;
        const saveData = getRemoveAttachmentsSaveData([id]);
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, getAttachments().find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
        const result = AppUtils.callService(() => taskService.taskEditRemoveAttachments(saveData.obj));
        if (!result) return;
        const taskDataRow = new TaskDataRow(props.taskDataRows[0]);
        taskDataRow.attachments = getAttachments().filter(i => i.id !== id);
        props.onChangeTaskDataRows([taskDataRow]);
    };

    const getAddAttachmentsSaveData = async (attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.taskId);
        result.append("companyId", props.companyId);
        result.append("subOwnerId", props.taskDataRows[0].id);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments));
        return result;
    };
    
    const handleAttachmentsAdd = async (fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        if (attachments.length < 1) return;
        const saveData = await getAddAttachmentsSaveData(attachments);
        const result = await AppUtils.callService(() => taskService.taskEditAddAttachments(saveData.obj));
        if (!result) return;
        EditItem.setItemIdsFromIds(attachments, result.ids);
        const taskDataRow = new TaskDataRow(props.taskDataRows[0]);
        taskDataRow.attachments = getAttachments().concat(attachments);
        props.onChangeTaskDataRows([taskDataRow]);
    };
    // #endregion Attachments

    const handleChangeTaskFromtDataCols = (dataCols: ITaskDataRowCol[]) => {
        const result = new TaskDataRow(props.taskDataRows[0]);
        result.cols = dataCols;
        props.onChangeTaskDataRows([result]);
    };

    return (
        <>
            {props.taskDataRows.length > 0 && props.taskDataRows[0].cols.length > 0 &&
                <Box sx={{ maxHeight: props.maxHeight, overflowY: "auto", overflowX: "hidden" }}>
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            isReadOnly={props.isLockedToOtherUser || props.isReadOnly}
                            isEdit={!props.isNew}
                            taskTypeCols={props.taskType.formCols}
                            taskDataRowCols={props.taskDataRows[0].cols}
                            onRefreshCols={handleChangeTaskFromtDataCols}
                        />
                    </TaskDynamicFormContainer>
                    {props.isNew &&
                        <Alert severity="info">{Translations.TaskMustBeSavedBeforeAttachmentsCanBeAdded}</Alert>
                    }
                    {!props.isNew &&
                        <AttachmentList
                            isReadOnly={props.isLockedToOtherUser || props.isReadOnly}
                            title={<label>{Translations.Attachments}</label>}
                            attachments={getAttachments()}
                            margin={"0.5rem 0 0 0"}
                            onRemoveAttachment={(id) => handleAttachmentRemove(id)}
                            onDownloadAttachment={handleAttachmentDownload}
                            onAddAttachments={(fileList) => handleAttachmentsAdd(fileList)}
                        />
                    }
                </Box>
            }
        </>
    );
};
